import {
  Outlet,
  createRootRoute,
  useRouter,
  useRouterState
} from '@tanstack/react-router'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX
} from 'react'
import { Toaster } from 'sonner'

import { AlertMe } from '../components/alert-me.tsx'
import { PagePalette } from '../components/page-palette.tsx'
import { SavedViewAlertHost } from '../components/saved-view-alert.tsx'
import { SaveViewOfferProvider } from '../components/save-view-offer.tsx'
import { LandingNavigationContext } from '../components/landing-navigation.tsx'
import {
  AppNav,
  SIDEBAR_CONTROL_CLASS,
  SidebarFooter
} from '../components/console-rail.tsx'
import { Button } from '../design-system/button.tsx'
import { EmptyState } from '../design-system/empty-state.tsx'
import { ButtonLink } from '../design-system/link.tsx'
import { Panel } from '../design-system/panel.tsx'
import { Icon } from '../design-system/icon.tsx'
import { NavSheet } from '../design-system/nav-sheet.tsx'
import { Notice } from '../design-system/notice.tsx'
import { usePreferences } from '../hooks/use-preferences.ts'
import { useViewerAccess } from '../hooks/use-viewer-access.ts'
import {
  accessibleNavSections,
  pageForPath,
  moveNavSection,
  navSectionsWithOperations,
  orderNavSections,
  hiddenAfterShowingAvailablePages,
  pinnedPages,
  railPages,
  groupInternalPages,
  toggleSectionHidden,
  type InternalNavSection
} from '../lib/pages.ts'
import { INTERNAL_PAGES } from './-nav.ts'
import { IDLE_WARNING_EVENT } from '../lib/idle.ts'
import {
  getPreferences,
  updatePreferences,
  type PreferencePatch
} from '../lib/preference-store.ts'
import { useSession } from '../session.tsx'

/**
 * What an address with no page shows, inside the console so the rail and
 * the menu still offer every page. The router's own default is a bare line
 * of text at the top left.
 *
 * @returns The panel, with the way back to the landing page.
 */
function PageNotFound(): JSX.Element {
  const router = useRouter()
  return (
    <Panel data-testid="not-found" title="Page not found">
      <EmptyState
        data-testid="not-found-empty"
        title="Nothing lives at this address"
        description="Check the address, or pick a page from the menu."
        action={
          <ButtonLink
            data-testid="not-found-home"
            href="/"
            variant="outline"
            onNavigate={() => void router.navigate({ to: '/' })}
          >
            Go to home
          </ButtonLink>
        }
      />
    </Panel>
  )
}

export const Route = createRootRoute({
  component: RootLayout,
  notFoundComponent: PageNotFound,
  errorComponent: function RouterError({ error }) {
    return (
      <div className="p-8">
        <Notice tone="danger" data-testid="router-error">
          {error.message}
        </Notice>
      </div>
    )
  }
})

/**
 * Every declared section, grouped once.
 *
 * `INTERNAL_PAGES` is frozen, so the grouping cannot change while the app
 * runs. `groupInternalPages` also returns the pages carrying no group; every
 * page here carries one and `-nav.test.ts` keeps it that way, so the rail
 * reads the sections alone. The framework keeps its flat branch for a rail
 * that may not be this one.
 *
 * A page with `inRail: false` opens only from a link and is left out here. A
 * pinned page is left out too: the rail's foot shows it instead.
 *
 * The rail shows these only after the backend's page access filters them, and
 * the reader's hidden pages apply after that.
 */
const NAV_SECTIONS = groupInternalPages(railPages(INTERNAL_PAGES)).sections

/** The pages the rail pins above the account row, before page access filters them. */
const PINNED_PAGES = pinnedPages(INTERNAL_PAGES)

/** An empty list, shared so a reader with no choice keeps one identity across renders. */
const NO_NAMES: readonly string[] = Object.freeze([])

/** What the rail says when a group or page change could not be held. */
const RAIL_UNSAVED_MESSAGE =
  'This change to the rail is not saved. It lasts until you reload.'

/**
 * A list with one name added, or removed when it is already there.
 *
 * @param list - The names held now.
 * @param name - The name to flip.
 * @returns A new list.
 */
function toggled(list: readonly string[], name: string): string[] {
  return list.includes(name)
    ? list.filter(entry => entry !== name)
    : [...list, name]
}

/** The shell follows super's desktop rail and mobile navigation sheet. */
function RootLayout(): JSX.Element {
  const router = useRouter()
  const { userName, error } = useSession()
  const access = useViewerAccess(userName)
  const availableSections = navSectionsWithOperations(
    accessibleNavSections(NAV_SECTIONS, access.data?.pageIds ?? NO_NAMES)
  )
  const allowedPinned = PINNED_PAGES.filter(page =>
    (access.data?.pageIds ?? NO_NAMES).includes(page.id)
  )
  const pathname = useRouterState({ select: state => state.location.pathname })
  const currentPage = pageForPath(INTERNAL_PAGES, pathname)
  const currentPageDenied =
    currentPage !== undefined &&
    access.data !== undefined &&
    !access.data.pageIds.includes(currentPage.id)
  const [menuOpen, setMenuOpen] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const onPaletteOpenChange = useCallback((open: boolean) => {
    setPaletteOpen(open)
    if (open) setMenuOpen(false)
  }, [])
  // Read from the settings store, not held in `AppNav`: the desktop rail and
  // the mobile sheet are two instances of it and must agree, and the choice
  // follows the employee to another machine (ADR-0014).
  const preferences = usePreferences()
  // Old Alerts and Platform collapse choices do not close the new Operations
  // section. It starts open until the reader closes Operations itself.
  const collapsedGroups = preferences.collapsedGroups ?? NO_NAMES
  const hiddenPages = preferences.hiddenPages ?? NO_NAMES
  const sections = orderNavSections(
    availableSections,
    preferences.sectionOrder ?? NO_NAMES
  )
  // Held here for the same reason: both rails show the same mode.
  const [customizing, setCustomizing] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const menuTrigger = useRef<HTMLButtonElement>(null)
  const content = useRef<HTMLElement>(null)

  useEffect(() => {
    const close = (): void => setMenuOpen(false)
    window.addEventListener(IDLE_WARNING_EVENT, close)
    return () => window.removeEventListener(IDLE_WARNING_EVENT, close)
  }, [])

  function closeMenu(): void {
    setMenuOpen(false)
  }

  // A change that is not held still applies to the rail. The reader is told,
  // and the choice simply does not outlive the tab.
  function saveRail(patch: PreferencePatch): void {
    void updatePreferences(patch).then(result => {
      setSaveError(result === 'saved' ? null : RAIL_UNSAVED_MESSAGE)
    })
  }

  function toggleGroup(group: string): void {
    saveRail({ collapsedGroups: toggled(collapsedGroups, group) })
  }

  function togglePage(pageId: string): void {
    saveRail({ hiddenPages: toggled(hiddenPages, pageId) })
  }

  const navProps = {
    pathname,
    onSelect: closeMenu,
    sections,
    collapsedGroups,
    onToggleGroup: toggleGroup,
    hiddenPages,
    onTogglePage: togglePage,
    onToggleSection: (section: InternalNavSection) =>
      saveRail({ hiddenPages: toggleSectionHidden(section, hiddenPages) }),
    onShowAllPages: () =>
      saveRail({
        hiddenPages: hiddenAfterShowingAvailablePages(sections, hiddenPages)
      }),
    onMoveSection: (group: string, direction: -1 | 1) => {
      const savedOrder = getPreferences().sectionOrder ?? NO_NAMES
      const current = orderNavSections(availableSections, savedOrder)
      saveRail({
        sectionOrder: moveNavSection(current, group, direction, savedOrder)
      })
    },
    customizing,
    onCustomizingChange: setCustomizing,
    saveError,
    onOpenSearch: () => onPaletteOpenChange(true),
    accessStatus:
      access.data !== undefined
        ? ('ready' as const)
        : access.isError
          ? ('error' as const)
          : ('loading' as const)
  }

  const openCustomization = useCallback(() => {
    setCustomizing(true)
    if (window.innerWidth < 1024) setMenuOpen(true)
  }, [])
  const landingNavigation = useMemo(
    () => ({ sections, hiddenPages, openCustomization }),
    [sections, hiddenPages, openCustomization]
  )
  // Search lists every page this viewer may open, including one they hid.
  // Hiding is a display choice, and that page still opens from its link.
  // A link-only page is in neither list, so search does not offer it.
  const palettePages = [
    ...sections.flatMap(section =>
      section.pages.map(page => ({ page, group: section.group }))
    ),
    ...allowedPinned.map(page => ({ page, group: page.group ?? 'Pinned' }))
  ]

  return (
    <div
      data-testid="app-root"
      className="bg-background text-foreground min-h-svh lg:grid lg:grid-cols-rail print:block!"
    >
      <aside
        data-testid="console-sidebar"
        className="bg-sidebar hidden lg:block print:hidden!"
      >
        {/* The aside stretches to the grid row, which is as tall as the report
            beside it. The foot pins to the viewport, not to that row, so the
            height and the stick belong on this inner box. */}
        <div className="sticky top-0 flex h-svh flex-col">
          <AppNav {...navProps} />
          <SidebarFooter
            onSelect={closeMenu}
            pathname={pathname}
            pages={allowedPinned}
          />
        </div>
      </aside>
      <div className="flex min-w-0 flex-col">
        <header
          data-testid="console-topbar"
          className="bg-sidebar sticky top-0 z-10 flex items-center gap-3 px-3 py-2 lg:hidden print:hidden"
        >
          <Button
            data-testid="console-nav-toggle"
            ref={menuTrigger}
            variant="quiet"
            size="icon"
            aria-label="Menu"
            aria-expanded={menuOpen}
            className={SIDEBAR_CONTROL_CLASS}
            onClick={() => setMenuOpen(true)}
          >
            <Icon name="menu" />
          </Button>
          <span className="text-sidebar-foreground/70 truncate text-sm">
            {userName}
          </span>
        </header>
        <NavSheet
          data-testid="console-nav"
          open={menuOpen}
          onOpenChange={setMenuOpen}
          title="Internal navigation"
          // The sheet is a flex column with a gap of its own, which would show
          // as a seam above the pinned foot.
          className="gap-0"
          onCloseAutoFocus={event => {
            event.preventDefault()
            const target = menuTrigger.current?.getClientRects().length
              ? menuTrigger.current
              : content.current
            target?.focus()
          }}
        >
          <AppNav {...navProps} mobile />
          <SidebarFooter
            mobile
            onSelect={closeMenu}
            pathname={pathname}
            pages={allowedPinned}
          />
        </NavSheet>
        <main
          ref={content}
          tabIndex={-1}
          data-testid="console-content"
          // Its own stacking layer, so a table's sticky header cells (up to
          // z-40) scroll under the mobile top bar rather than over it.
          className="isolate flex min-w-0 flex-col gap-6 p-4 sm:p-6 md:p-8 print:p-0!"
        >
          {error ? (
            <Notice tone="danger" data-testid="session-error">
              {error}
            </Notice>
          ) : null}
          {currentPage === undefined && pathname !== '/' ? (
            <Outlet />
          ) : access.data === undefined ? (
            access.isError ? (
              <Notice data-testid="page-access-error" tone="danger">
                Page access could not be checked. Reload this page to try again.
              </Notice>
            ) : (
              <Notice data-testid="page-access-loading" tone="info">
                Checking page access…
              </Notice>
            )
          ) : currentPageDenied &&
            currentPage !== undefined &&
            access.data.unavailablePageIds.includes(currentPage.id) ? (
            <Notice data-testid="page-access-unavailable" tone="warning">
              Page access is temporarily unavailable. Reload this page to try
              again.
            </Notice>
          ) : currentPageDenied ? (
            <Panel data-testid="page-access-denied" title="Page unavailable">
              <EmptyState
                data-testid="page-access-denied-message"
                title="You do not have access to this page"
                description="Ask the page owner for access to a listed group."
              />
            </Panel>
          ) : (
            <>
              {access.isError ? (
                <Notice data-testid="page-access-warning" tone="warning">
                  Page access could not be refreshed. Your last access check is
                  still in use.
                </Notice>
              ) : access.data.reason ? (
                <Notice data-testid="page-access-warning" tone="warning">
                  Some pages are unavailable because group membership could not
                  be checked.
                </Notice>
              ) : null}
              <LandingNavigationContext.Provider value={landingNavigation}>
                <SaveViewOfferProvider>
                  <SavedViewAlertHost>
                    <AlertMe />
                    <Outlet />
                  </SavedViewAlertHost>
                </SaveViewOfferProvider>
              </LandingNavigationContext.Provider>
            </>
          )}
        </main>
        <Toaster />
      </div>
      <PagePalette
        open={paletteOpen}
        onOpenChange={onPaletteOpenChange}
        pages={palettePages}
        enabled={access.data !== undefined}
        onNavigate={path => {
          router.history.push(path)
        }}
      />
    </div>
  )
}
